// This module is imported by every desktop pack (step1-4, booking) on every
// brand, so anything at the top of it is downloaded, parsed and evaluated on
// every page of the site. It used to open with nine such imports --
// fancybox, air-datepicker, Font Awesome, jQuery UI plus three of its
// widgets, Popper, Bootstrap and tooltipster -- none of which the rebuilt
// funnel calls any more:
//
//   air-datepicker      the search field is datepicker_controller.js now
//   jQuery UI widgets   autocomplete_controller.js replaced .autocomplete();
//                       .selectmenu()/.slider() survive only in the separate
//                       iframe pack, which imports jQuery UI itself
//   Font Awesome        its last four glyphs are inline SVG now
//                       (common/_travel_mode_icon.html.erb)
//   Bootstrap + Popper  one dropdown in the fixtravel layout -- see
//                       legacy_bootstrap.js
//   fancybox/tooltipster only reached from the `else` arms the .tw-page
//                       checks skip -- see legacy_plugins.js
//
// The dead ones are gone; the two that some page somewhere still needs load
// on demand at the point of use. jQuery itself stays: .js.erb responses and
// a few controllers still use it, and jquery-ujs still backs remote forms.
import $ from "jquery";
import {} from "jquery-ujs";

import Honeybadger from "honeybadger-js";

import { loadLegacyPlugins } from "./legacy_plugins";
import { loadLegacyBootstrap } from "./legacy_bootstrap";

import "../../dep/jquery.resizeandcrop";
import "../../dep/gon";

import { Track } from "../../components/common/track";
import { Translation } from "../../components/common/translation";
import { logFunnelEvent } from "../../app/helpers/classes/funnel_tracking";

import Modal from './modal'

Honeybadger.configure({
  apiKey: "780ab0c8",
  environment: process.env.ENVIRONMENT || "development",
  revision: "master",
  disabled: !(process.env.HB_DISABLED === "false"),
  maxErrors: 100
});

Honeybadger.beforeNotify(function (notice) {
  if (/api.tiles.mapbox.com|cdn.livechatinc.com/.test(notice.stack)) {
    return false;
  }
});

// Wraps the existing (otherwise-bare) #alert-message flash content in a
// Tailwind dialog shell at runtime, instead of duplicating that content
// server-side just to have two markup shapes for one flash partial shared
// by every layout. Moves contentEl in place rather than cloning it, so
// its content (including any links/markup inside) stays exactly as
// rendered.
function showAlertMessageDialog(contentEl) {
  const backdrop = document.createElement("div");
  backdrop.className = "fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4";

  const card = document.createElement("div");
  card.className = "w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl";

  const header = document.createElement("div");
  header.className = "flex items-center justify-end border-b border-gray-200 px-4 py-3";

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "text-gray-400 hover:text-gray-600";
  closeBtn.setAttribute("aria-label", "Stäng");
  closeBtn.innerHTML = '<svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>';

  const body = document.createElement("div");
  body.className = "p-6 text-sm text-gray-700";

  const close = () => backdrop.remove();
  closeBtn.addEventListener("click", close);
  backdrop.addEventListener("click", (event) => {
    if (event.target === backdrop) close();
  });

  header.appendChild(closeBtn);
  contentEl.style.display = "";
  body.appendChild(contentEl);
  card.appendChild(header);
  card.appendChild(body);
  backdrop.appendChild(card);
  document.body.appendChild(backdrop);
}
window.showAlertMessageDialog = showAlertMessageDialog;

function ready(fn) {
  if (document.readyState !== "loading") fn();
  else document.addEventListener("DOMContentLoaded", fn);
}

ready(function() {
  // Bootstrap's own data-api wires itself up on import, so the markup being
  // present is the whole condition -- see legacy_bootstrap.js.
  if (document.querySelector("[data-toggle]")) loadLegacyBootstrap();

  Track.initialize();
  window.modal = new Modal()
  window.modal.init()

  const alertMessage = document.getElementById("alert-message");
  if (alertMessage) {
    logFunnelEvent("error_popup_shown", { message: alertMessage.textContent.trim() });

    // Rebuilt Tailwind pages (.tw-page) get a Tailwind dialog instead of
    // fancybox -- the site-wide fancybox replacement (see
    // step2_seating_fix memory). Not scoped to any single page: this
    // flash partial is shared by every layout, rebuilt or not, so the
    // branch has to happen here at runtime rather than in the (also
    // shared) markup itself. The fancybox arm is still reachable for the
    // brands whose layouts don't mark .tw-page.
    if (document.querySelector(".tw-page")) {
      showAlertMessageDialog(alertMessage);
    } else {
      loadLegacyPlugins().then(() => $.fancybox.open({ src: "#alert-message" }));
    }
  }

  // A .languages tooltipster dropdown used to be initialised here. Neither
  // `.languages` nor the `#language_dropdown_window` it read its content
  // from exists in any view -- this file was their only mention anywhere in
  // the app -- so it had been dead for some time. Language switching is
  // part of the rebuilt header markup now.
});
